import * as THREE from 'three/webgpu';

import type { MeasureEntityRef } from '../types/measure-types';
import { cloneMeasureEntityRef } from '../types/measure-types';
import type { MeasureReferencePolicy } from '../types/measure-reference-policy-types';
import { resolveMeasureEntityRefFromProgrammaticPick } from './MeasureSelectionBootstrap';
import type { MeasurePickSelectionPolicy } from './MeasurePickSelectionPolicy';

/**
 * 程序化测量选中应用结果。
 */
export interface MeasureProgrammaticSelectionApplyResult {
    /** 下一帧选中集。 */
    entities: MeasureEntityRef[];
    /** 下一帧拾取历史。 */
    pickHistory: MeasureEntityRef[];
    /** 是否至少成功接纳一个实体。 */
    accepted: boolean;
}

/**
 * 测量程序化选中应用器。
 *
 * 职责：将设计树/API 的程序化命中统一转换为测量选中集变更结果。
 */
export class MeasureProgrammaticSelectionApplier {
    /**
     * 将程序化命中列表应用到当前测量选中集。
     *
     * 设计说明：
     * 1. 复用 `MeasurePickSelectionPolicy`，保证与视口点击 toggle/双选滑动替换一致；
     * 2. 使用 bootstrap 的实体解析规则，覆盖基准几何等无 BRep 描述符对象。
     *
     * @param currentEntities 当前选中集。
     * @param currentPickHistory 当前拾取历史。
     * @param incomingPicks 待写入的程序化命中。
     * @param referencePolicy 双选参考点策略。
     * @param pickSelectionPolicy 拾取后选中集变更策略。
     * @returns 应用结果。
     */
    public applyPicks(
        currentEntities: readonly MeasureEntityRef[],
        currentPickHistory: readonly MeasureEntityRef[],
        incomingPicks: readonly THREE.Intersection<THREE.Object3D>[],
        referencePolicy: MeasureReferencePolicy,
        pickSelectionPolicy: MeasurePickSelectionPolicy,
    ): MeasureProgrammaticSelectionApplyResult {
        let entities = currentEntities.map((entity) => cloneMeasureEntityRef(entity));
        let pickHistory = currentPickHistory.map((entry) => cloneMeasureEntityRef(entry));
        let accepted = false;

        for (const pick of incomingPicks) {
            const entityRef = resolveMeasureEntityRefFromProgrammaticPick(pick);
            if (!entityRef) {
                continue;
            }

            const nextEntities = pickSelectionPolicy.resolveNextSelection(
                entities,
                entityRef,
                referencePolicy,
            );

            pickHistory.push(cloneMeasureEntityRef(entityRef));
            entities = nextEntities;
            pickHistory = pickSelectionPolicy.prunePickHistory(pickHistory, entities);
            accepted = true;
        }

        return {
            entities,
            pickHistory,
            accepted,
        };
    }
}
